<template>
	<div class="inline-block">
		<!-- Important modifiers: Dropdown hardcodes `absolute right-0`, which on a narrow screen puts this wide menu off-screen. -->
		<Dropdown ref="dropdown" class="!fixed !inset-x-2 sm:!absolute sm:!left-auto sm:!right-0">
			<template #trigger>
				<button type="button" id="support-hub-btn" data-closes-chat class="btn btn-ghost btn-circle btn-sm border-none">
					<QuestionMarkCircleIcon class="w-7 h-7" />
				</button>
			</template>
			<template #content>
				<DropdownMenu class="flex flex-col gap-2 w-auto sm:w-96 p-2 max-h-[calc(100dvh-5rem)] overflow-y-auto sm:max-h-none sm:overflow-visible">
					<DropdownMenuItem type="button" class="border-none">
						<a @click="selectOption(() => showKnowledgeBase = true)" class="flex items-start justify-start w-full">
							<div class="w-8 h-8 mr-2 shrink-0">
								<!-- phosphor icon - book open text -->
								<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256">
									<path d="M232,50H160a38,38,0,0,0-32,17.55A38,38,0,0,0,96,50H24a6,6,0,0,0-6,6V200a6,6,0,0,0,6,6H96a26,26,0,0,1,26,26,6,6,0,0,0,12,0,26,26,0,0,1,26-26h72a6,6,0,0,0,6-6V56A6,6,0,0,0,232,50ZM96,194H30V62H96a26,26,0,0,1,26,26V204.31A37.86,37.86,0,0,0,96,194Zm130,0H160a37.87,37.87,0,0,0-26,10.32V88a26,26,0,0,1,26-26h66ZM160,90h40a6,6,0,0,1,0,12H160a6,6,0,0,1,0-12Zm46,38a6,6,0,0,1-6,6H160a6,6,0,0,1,0-12h40A6,6,0,0,1,206,128Zm0,32a6,6,0,0,1-6,6H160a6,6,0,0,1,0-12h40A6,6,0,0,1,206,160Z"></path>
								</svg>
							</div>
							<div>
								<div class="text-lg font-semibold">
									{{ $t('User guide') }}
								</div>
								<div class="text-sm">
									{{ $t('Learn how to get started and get the most out of Smart Symbols') }}
								</div>
							</div>
						</a>
					</DropdownMenuItem>
					<DropdownMenuItem v-if="currentOrganisation?.subscription_type === 'internal'" type="button" class="border-none">
						<a @click="selectOption(openChat)" class="flex items-start justify-start w-full">
							<div class="w-8 h-8 mr-2 shrink-0">
								<!-- phosphor icon - chat circle dots -->
								<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256">
									<path d="M138,128a10,10,0,1,1-10-10A10,10,0,0,1,138,128ZM84,118a10,10,0,1,0,10,10A10,10,0,0,0,84,118Zm88,0a10,10,0,1,0,10,10A10,10,0,0,0,172,118Zm58,10A102,102,0,0,1,79.31,217.65L44.44,229.27a14,14,0,0,1-17.71-17.71l11.62-34.87A102,102,0,1,1,230,128Zm-12,0A90,90,0,1,0,50.08,173.06a6,6,0,0,1,.5,4.91L38.12,215.35a2,2,0,0,0,2.53,2.53L78,205.42a6.2,6.2,0,0,1,1.9-.31,6.09,6.09,0,0,1,3,.81A90,90,0,0,0,218,128Z"></path>
								</svg>
							</div>
							<div>
								<div class="text-lg font-semibold">
									{{ $t('Chat') }}
								</div>
								<div class="text-sm">
									{{ $t('Ask your question via chat and get an answer right away') }}
								</div>
							</div>
						</a>
					</DropdownMenuItem>
					<DropdownMenuItem type="button" v-if="$i18n.locale.split(/[-_]/)[0].toLowerCase() === 'nl'" class="border-none">
						<a href="https://pages.smartsymbols.com/webinars" target="_blank" rel="noopener noreferrer" class="flex items-start justify-start w-full">
							<div class="w-8 h-8 mr-2 shrink-0">
								<!-- phosphor icon - youtube logo -->
								<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256">
									<path d="M163.33,123l-48-32A6,6,0,0,0,106,96v64a6,6,0,0,0,9.33,5l48-32a6,6,0,0,0,0-10ZM118,148.79V107.21L149.18,128ZM232.4,70a22,22,0,0,0-13.28-15C185,41.79,130.27,42,128,42s-57-.21-91.16,13A22,22,0,0,0,23.6,70C21.05,79.89,18,98,18,128s3.05,48.11,5.6,58a22,22,0,0,0,13.28,15C71,214.21,125.72,214,128,214h.71c6.91,0,58-.44,90.45-13a22,22,0,0,0,13.28-15c2.55-9.87,5.6-27.93,5.6-58S235,79.89,232.4,70ZM220.78,183a10,10,0,0,1-6,6.86C182.78,202.19,128.58,202,128,202s-54.71.2-86.75-12.17a10,10,0,0,1-6-6.86C32.84,173.78,30,156.78,30,128s2.84-45.78,5.22-55a10,10,0,0,1,6-6.86C72.06,54.26,123.53,54,127.76,54H128c.54,0,54.71-.2,86.75,12.17a10,10,0,0,1,6,6.86c2.38,9.19,5.22,26.19,5.22,55S223.16,173.78,220.78,183Z"></path>
								</svg>
							</div>
							<div>
								<div class="text-lg font-semibold">
									{{ $t("Webinars") }}
								</div>
								<div class="text-sm">
									{{ $t('Learn from recorded webinars, or subscribe for upcoming webinars') }}
								</div>
							</div>
						</a>
					</DropdownMenuItem>
					<DropdownMenuItem type="button" class="border-none">
						<a @click="selectOption(openFeedback)" class="flex items-start justify-start w-full">
							<div class="relative shrink-0 h-8 w-8 mr-2">
								<!-- phosphor icon - thumbs up -->
								<div class="absolute top-1 left-0 w-4 h-4">
									<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256">
										<path d="M237,77.47A28,28,0,0,0,216,68H164V56a44.05,44.05,0,0,0-44-44,12,12,0,0,0-10.73,6.63L72.58,92H32a20,20,0,0,0-20,20v88a20,20,0,0,0,20,20H204a28,28,0,0,0,27.78-24.53l12-96A28,28,0,0,0,237,77.47ZM36,116H68v80H36ZM220,96.5l-12,96a4,4,0,0,1-4,3.5H92V106.83L126.82,37.2A20,20,0,0,1,140,56V80a12,12,0,0,0,12,12h64a4,4,0,0,1,4,4.5Z"></path>
									</svg>
								</div>

								<!-- phosphor icon - thumbs down -->
								<div class="absolute bottom-1 right-0 w-4 h-4 -scale-x-100">
									<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256">
										<path d="M243.78,156.53l-12-96A28,28,0,0,0,204,36H32A20,20,0,0,0,12,56v88a20,20,0,0,0,20,20H72.58l36.69,73.37A12,12,0,0,0,120,244a44.05,44.05,0,0,0,44-44V188h52a28,28,0,0,0,27.78-31.47ZM68,140H36V60H68Zm151,22.65a4,4,0,0,1-3,1.35H152a12,12,0,0,0-12,12v24a20,20,0,0,1-13.18,18.8L92,149.17V60H204a4,4,0,0,1,4,3.5l12,96A4,4,0,0,1,219,162.65Z"></path>
									</svg>
								</div>
							</div>
							<div>
								<div class="text-lg font-semibold">
									{{ $t("Feedback") }}
								</div>
								<div class="text-sm">
									{{ $t('Let us know what you like or what we should improve') }}
								</div>
							</div>
						</a>
					</DropdownMenuItem>
					<div class="flex items-center gap-2 text-xs border-none px-2">
						<EnvelopeIcon class="w-4 h-4"></EnvelopeIcon>
						<p v-html="$t('Or send an e-mail to {email}', { email: '<a href=\'mailto:support@smartsymbols.com\' target=\'_blank\' class=\'font-bold underline\'>support@smartsymbols.com</a>' })"></p>
					</div>
				</DropdownMenu>
			</template>
		</Dropdown>
	</div>
	<KBView v-if="showKnowledgeBase" :url="knowledgeBaseUrl" @requestClose="showKnowledgeBase = false" />
</template>

<script lang="ts">
import Dropdown from "./ui/Dropdown.vue";
import DropdownMenu from "./ui/DropdownMenu.vue";
import DropdownMenuItem from "./ui/DropdownMenuItem.vue";
import KBView from "@/components/KBView.vue";
import { useOrganisationsStore } from "@/stores/Organisations.store";
import { productFruits } from "product-fruits";
import {
	QuestionMarkCircleIcon,
	EnvelopeIcon
} from "@heroicons/vue/24/outline";
import { mapState } from "pinia";

const knowledgeBaseUrls = {
	en: "https://handsomely-cicada-2cb.notion.site/ebd/21e0964c114280d9ac5ecf22bb565cc1",
	nl: "https://handsomely-cicada-2cb.notion.site/ebd/26c0964c1142809fb5d0e1513cc3e14d",
};

export default {
	name: "SupportHubButton",
	emits: ["openChat"],

	components: {
		QuestionMarkCircleIcon,
		EnvelopeIcon,
		Dropdown,
		DropdownMenu,
		DropdownMenuItem,
		KBView,
	},

	data() {
		return {
			showKnowledgeBase: false,
		};
	},

	computed: {
		...mapState(useOrganisationsStore, ["currentOrganisation"]),

		// Get the appropriate knowledge base URL based on the current locale
		knowledgeBaseUrl(): string {
			const baseLocale = this.$i18n.locale.split(/[-_]/)[0].toLowerCase();
			return knowledgeBaseUrls[baseLocale as keyof typeof knowledgeBaseUrls] ?? knowledgeBaseUrls.en;
		},
	},

	methods: {
		selectOption(action: () => void) {
			action();
			(this.$refs.dropdown as any)?.close?.();
		},

		openChat() {
			this.$emit("openChat");
		},

		openFeedback() {
			productFruits.safeExec(() => {
				// Anchoring to the top-right only makes sense once there is room beside the button.
				const position = window.innerWidth < 640 ? undefined : { top: '84px', right: '20px' };
				window.productFruits?.api?.feedback?.showModal(position);
			});
		},
	},
};
</script>
